import useTheme from '@material-ui/core/styles/useTheme';
import {UeTheme} from '../ueTheme.types';
import {createSharedValueKey, useSharedValue} from '@epic-core/hooks';
import {
    AccentColorOptions,
    AccentVariantOptions,
    CombinedTextColorOptions,
    TextColorOptions
} from '../types';
import {ueGradientColors} from '../ueCommonTheme';
import {useEffect} from 'react';

export const accentColors: AccentColorOptions[] = [
    'forest',
    'fire',
    'galaxy',
    'sky',
    'orchid',
    'neon'
];
export const defaultAccentColor = 'sky';

const textColors = ['primary', 'secondary', 'disabled', 'hint'];

interface AccentColorStyleHookResponse {
    style: {color?: string};
    validAccentColor: boolean;
    themeAccentColor?: string;
}

export const currentAccentColorKey = createSharedValueKey<AccentColorOptions>(
    'currentAccentColor',
    defaultAccentColor
);

export const useAccentColor = (accentColor: AccentColorOptions): string => {
    const theme: UeTheme = useTheme();

    if (accentColor && accentColors.includes(accentColor)) {
        return theme.palette.accent[accentColor];
    }

    return '';
};

export interface CurrentAccentColorProps {
    name: AccentColorOptions;
    color: string;
    from: string;
    to: string;
}

export const useCurrentAccentColor = (): CurrentAccentColorProps => {
    const [currentAccentColor] = useSharedValue(currentAccentColorKey);
    const accentColorHex = useAccentColor(currentAccentColor);
    const gradient = ueGradientColors[currentAccentColor] || ueGradientColors.sky;

    const current: CurrentAccentColorProps = {
        name: currentAccentColor,
        color: accentColorHex,
        ...gradient
    };

    return current;
};

export const useSetCurrentAccentColor = (accentColor: AccentColorOptions = 'sky'): void => {
    const [currentAccentColor, setCurrentAccentColor] = useSharedValue(currentAccentColorKey);

    useEffect(() => {
        if (accentColor === currentAccentColor) return;
        if (!accentColor) return;
        setCurrentAccentColor(accentColor);
    }, [setCurrentAccentColor, currentAccentColor, accentColor]);
};

export const useAccentVariant = (
    accentColor: AccentColorOptions,
    accentVariant?: AccentVariantOptions
): string => {
    const theme: UeTheme = useTheme();

    if (accentColor && accentColors.includes(accentColor)) {
        if (accentVariant === 'dark') {
            return theme.palette.accent.dark[accentColor];
        } else if (accentVariant === 'bright') {
            return theme.palette.accent.bright[accentColor];
        }
        return theme.palette.accent[accentColor];
    }

    return '';
};

export const useTextColor = (color: TextColorOptions): string => {
    const theme: UeTheme = useTheme();

    if (color && textColors.includes(color)) {
        return theme.palette.text[color];
    }

    return '';
};

export const useCombinedTextColor = (color?: CombinedTextColorOptions): string => {
    const theme: UeTheme = useTheme();

    if (color && accentColors.includes(color as AccentColorOptions)) {
        return theme.palette.accent[color as AccentColorOptions];
    }

    if (color && textColors.includes(color as TextColorOptions)) {
        return theme.palette.text[color as TextColorOptions];
    }

    return 'inherit';
};

export const useAccentColorStyle = (
    accentColor: AccentColorOptions
): AccentColorStyleHookResponse => {
    const style: {color?: string} = {};

    const themeAccentColor = useAccentColor(accentColor);
    const textColor = useTextColor(accentColor as TextColorOptions);
    const validAccentColor = !!themeAccentColor;

    if (themeAccentColor) {
        style.color = themeAccentColor;
    } else if (textColor) {
        style.color = textColor;
    }

    return {style, validAccentColor, themeAccentColor};
};
